import React, { useState, useEffect } from 'react';
import { useLang } from '../../contexts/LanguageContext';
import { X, Users, Search, Trash2, Check, Shield, Activity, CheckSquare, Square } from 'lucide-react';
import { EMZI_COLORS, EMZI_SHADOWS, EMZI_RADIUS } from '../../styles/emziCI';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';

/**
 * EMZI NEXUS V6.3 - Manage Group Modal (Phase 2)
 * Admin interface for managing group members, permissions, and activity
 * - Members Tab: View/add/remove members with BULK operations
 * - Permissions Tab: Module access control
 * - Activity Log Tab: Group activity history
 */

const ManageGroupModal = ({ group, onClose, onUpdate }) => {
  const t = useLang();
  const [activeTab, setActiveTab] = useState('members');
  
  // Members state
  const [members, setMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  
  // Permissions state
  const [permissions, setPermissions] = useState([]);
  const [permissionsSaving, setPermissionsSaving] = useState(false);
  
  // Activity log state
  const [activityLogs, setActivityLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const MODULES = [
    { id: 'kpm', name: t('admin.permissions.modules.kpm'), dbName: 'Knowledge Pack Manager' },
    { id: 'studios', name: t('admin.permissions.modules.studios'), dbName: 'Studios' },
    { id: 'nexus_academy', name: t('admin.permissions.modules.nexus_academy'), dbName: 'Nexus Academy' },
    { id: 'queue', name: t('admin.permissions.modules.queue'), dbName: 'Queue' },
    { id: 'analytics', name: t('admin.permissions.modules.analytics'), dbName: 'Analytics' },
    { id: 'communications', name: t('admin.permissions.modules.communications'), dbName: 'Communications' }
  ];

  // Helper function to normalize module names to IDs
  const normalizeModulesToIds = (modules) => {
    if (!modules) return [];
    return modules.map(mod => {
      // Check if it's already an ID
      const foundById = MODULES.find(m => m.id === mod);
      if (foundById) return mod;
      // Otherwise find by dbName
      const foundByName = MODULES.find(m => m.dbName === mod || m.dbName.toLowerCase() === mod.toLowerCase());
      return foundByName ? foundByName.id : mod;
    });
  };

  // Helper function to convert IDs back to dbNames for saving
  const convertIdsToDbNames = (moduleIds) => {
    return moduleIds.map(id => {
      const found = MODULES.find(m => m.id === id);
      return found ? found.dbName : id;
    });
  };

  useEffect(() => {
    if (group) {
      fetchMembers();
      // Normalize the loaded permissions to IDs
      setPermissions(normalizeModulesToIds(group.allowed_modules || []));
    }
  }, [group]);

  useEffect(() => {
    if (activeTab === 'activity') {
      fetchActivityLogs();
    }
  }, [activeTab]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ============================================================
  // MEMBERS TAB FUNCTIONS
  // ============================================================

  const fetchMembers = async () => {
    try {
      setLoading(true);
      setError(null);
            const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/functional-groups/${group.group_id}/members`,
        {
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || `Error ${response.status}`;
        console.error('Failed to fetch members:', response.status, errorMsg);
        setError(`Failed to load members: ${errorMsg}`);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
      setError('Network error loading members');
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
            const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/users/search?q=${encodeURIComponent(query)}&exclude_group_id=${group.group_id}`,
        {
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
        setSelectedUsers(new Set()); // Clear selections on new search
      }
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setSearching(false);
    }
  };

  const toggleUserSelection = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === searchResults.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(searchResults.map(u => u.user_id)));
    }
  };

  const bulkAddMembers = async (roleLayer = 'Executor') => {
    if (selectedUsers.size === 0) {
      showToast(t('admin.bulk.noSelection'), 'error');
      return;
    }

    if (!window.confirm(t('admin.bulk.confirmBulkAdd'))) {
      return;
    }

    try {
            const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/functional-groups/${group.group_id}/members/bulk`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_ids: Array.from(selectedUsers),
            role_layer: roleLayer
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        showToast(`${data.data.added_count} ${t('admin.bulk.bulkSuccess')}`, 'success');
        setSelectedUsers(new Set());
        setSearchQuery('');
        setSearchResults([]);
        fetchMembers();
        if (onUpdate) onUpdate();
      } else {
        showToast(t('admin.bulk.bulkError'), 'error');
      }
    } catch (err) {
      console.error('Error bulk adding members:', err);
      showToast(t('admin.bulk.bulkError'), 'error');
    }
  };

  const bulkRemoveMembers = async () => {
    if (selectedUsers.size === 0) {
      showToast(t('admin.bulk.noSelection'), 'error');
      return;
    }

    if (!window.confirm(t('admin.bulk.confirmBulkRemove'))) {
      return;
    }

    try {
            const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/functional-groups/${group.group_id}/members/bulk`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_ids: Array.from(selectedUsers)
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        showToast(`${data.data.removed_count} users removed`, 'success');
        setSelectedUsers(new Set());
        fetchMembers();
        if (onUpdate) onUpdate();
      } else {
        showToast(t('admin.bulk.bulkError'), 'error');
      }
    } catch (err) {
      console.error('Error bulk removing members:', err);
      showToast(t('admin.bulk.bulkError'), 'error');
    }
  };

  const removeMember = async (userId) => {
    if (!window.confirm(t('admin.members.confirmRemove'))) {
      return;
    }

    try {
            const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/functional-groups/${group.group_id}/members/${userId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          }
        }
      );

      if (response.ok) {
        showToast('Member removed', 'success');
        fetchMembers();
        if (onUpdate) onUpdate();
      }
    } catch (err) {
      console.error('Error removing member:', err);
      showToast('Failed to remove member', 'error');
    }
  };

  const updateMemberRole = async (userId, newRole) => {
    try {
            // Convert display role to system role format
      const roleMap = {
        'Management': 'management_functional',
        'Supervisor': 'supervisor',
        'Executor': 'executor'
      };
      const systemRole = roleMap[newRole] || newRole.toLowerCase();
      
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/functional-groups/${group.group_id}/members/${userId}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ role: systemRole })
        }
      );

      if (response.ok) {
        showToast('Role updated successfully', 'success');
        fetchMembers();
        if (onUpdate) onUpdate();
      } else {
        const errorData = await response.json();
        showToast(errorData.detail || 'Failed to update role', 'error');
      }
    } catch (err) {
      console.error('Error updating role:', err);
      showToast('Failed to update role', 'error');
    }
  };

  // ============================================================
  // PERMISSIONS TAB FUNCTIONS
  // ============================================================

  const togglePermission = (moduleId) => {
    setPermissions(prev => {
      if (prev.includes(moduleId)) {
        return prev.filter(m => m !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  };

  const savePermissions = async () => {
    try {
      setPermissionsSaving(true);
            // Convert IDs to database format (display names) before saving
      const modulesToSave = convertIdsToDbNames(permissions);
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/functional-groups/${group.group_id}/permissions`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ allowed_modules: modulesToSave })
        }
      );

      if (response.ok) {
        showToast(t('admin.permissions.permissionsUpdated'), 'success');
        if (onUpdate) onUpdate();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Permission save error:', errorData);
        showToast(t('admin.permissions.permissionsFailed'), 'error');
      }
    } catch (err) {
      console.error('Error saving permissions:', err);
      showToast(t('admin.permissions.permissionsFailed'), 'error');
    } finally {
      setPermissionsSaving(false);
    }
  };

  // ============================================================
  // ACTIVITY LOG TAB FUNCTIONS
  // ============================================================

  const fetchActivityLogs = async () => {
    try {
      setLogsLoading(true);
            const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/audit-logs?group_id=${group.group_id}&limit=20`,
        {
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setActivityLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Error fetching activity logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionBadgeColor = (action) => {
    if (action.includes('ADDED')) return 'bg-green-100 text-green-800';
    if (action.includes('REMOVED')) return 'bg-red-100 text-red-800';
    if (action.includes('UPDATED')) return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  // ============================================================
  // RENDER FUNCTIONS
  // ============================================================

  const renderMembersTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Add User Section with Bulk Operations */}
      <div style={{
        background: 'white',
        borderRadius: EMZI_RADIUS.md,
        padding: '20px',
        boxShadow: EMZI_SHADOWS.card
      }}>
        <h3 style={{ 
          fontSize: '16px', 
          fontWeight: '600', 
          marginBottom: '12px',
          color: EMZI_COLORS.textPrimary
        }}>
          {t('admin.members.addUser')}
        </h3>
        
        <div style={{ position: 'relative', marginBottom: '12px' }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: EMZI_COLORS.textSecondary
          }} />
          <input
            type="text"
            placeholder={t('admin.members.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              searchUsers(e.target.value);
            }}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.sm,
              fontSize: '14px'
            }}
          />
        </div>

        {searching && (
          <div style={{ textAlign: 'center', padding: '12px', color: EMZI_COLORS.textSecondary }}>
            {t('common.loading')}
          </div>
        )}

        {searchResults.length > 0 && (
          <>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '8px',
              padding: '8px 0'
            }}>
              <button
                onClick={toggleSelectAll}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  color: EMZI_COLORS.primary,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {selectedUsers.size === searchResults.length ? (
                  <CheckSquare size={16} />
                ) : (
                  <Square size={16} />
                )}
                {selectedUsers.size === searchResults.length 
                  ? t('admin.bulk.deselectAll')
                  : t('admin.bulk.selectAll')}
              </button>
              
              {selectedUsers.size > 0 && (
                <span style={{
                  fontSize: '13px',
                  color: EMZI_COLORS.textSecondary,
                  fontWeight: '500'
                }}>
                  {selectedUsers.size} {t('admin.bulk.selectedCount')}
                </span>
              )}
            </div>

            <div style={{
              maxHeight: '250px',
              overflowY: 'auto',
              border: `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.sm,
              marginBottom: '12px'
            }}>
              {searchResults.map((user) => (
                <div
                  key={user.user_id}
                  onClick={() => toggleUserSelection(user.user_id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    borderBottom: `1px solid ${EMZI_COLORS.border}`,
                    cursor: 'pointer',
                    background: selectedUsers.has(user.user_id) ? EMZI_COLORS.background : 'white',
                    transition: 'background 0.2s'
                  }}
                >
                  <div>
                    {selectedUsers.has(user.user_id) ? (
                      <CheckSquare size={20} color={EMZI_COLORS.primary} />
                    ) : (
                      <Square size={20} color={EMZI_COLORS.textSecondary} />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: EMZI_COLORS.textPrimary }}>
                      {user.name}
                    </div>
                    <div style={{ fontSize: '12px', color: EMZI_COLORS.textSecondary }}>
                      {user.email}
                    </div>
                  </div>
                  <span style={{
                    fontSize: '11px',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    background: EMZI_COLORS.background,
                    color: EMZI_COLORS.textSecondary,
                    fontWeight: '500'
                  }}>
                    {user.role}
                  </span>
                </div>
              ))}
            </div>

            {selectedUsers.size > 0 && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => bulkAddMembers('Executor')}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    background: EMZI_COLORS.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: EMZI_RADIUS.sm,
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Check size={16} />
                  {t('admin.bulk.bulkAdd')} ({selectedUsers.size})
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Current Members Section */}
      <div style={{
        background: 'white',
        borderRadius: EMZI_RADIUS.md,
        padding: '20px',
        boxShadow: EMZI_SHADOWS.card
      }}>
        <h3 style={{ 
          fontSize: '16px', 
          fontWeight: '600', 
          marginBottom: '16px',
          color: EMZI_COLORS.textPrimary
        }}>
          {t('admin.members.currentMembers')} ({members.length})
        </h3>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '24px', color: EMZI_COLORS.textSecondary }}>
            {t('common.loading')}
          </div>
        ) : members.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px', color: EMZI_COLORS.textSecondary }}>
            {t('admin.members.noMembers')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {members.map((member) => (
              <div
                key={member.user_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  border: `1px solid ${EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.sm,
                  background: 'white'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: EMZI_COLORS.textPrimary }}>
                    {member.name || member.email}
                  </div>
                  <div style={{ fontSize: '12px', color: EMZI_COLORS.textSecondary }}>
                    {member.email}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <select
                    value={(() => {
                      // Map system role to display value
                      const roleDisplayMap = {
                        'management_functional': 'Management',
                        'management': 'Management',
                        'supervisor': 'Supervisor',
                        'executor': 'Executor'
                      };
                      return roleDisplayMap[member.role?.toLowerCase()] || member.role || 'Executor';
                    })()}
                    onChange={(e) => updateMemberRole(member.user_id, e.target.value)}
                    style={{
                      padding: '6px 10px',
                      border: `1px solid ${EMZI_COLORS.border}`,
                      borderRadius: EMZI_RADIUS.sm,
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="Management">{t('admin.roles.management')}</option>
                    <option value="Supervisor">{t('admin.roles.supervisor')}</option>
                    <option value="Executor">{t('admin.roles.executor')}</option>
                  </select>

                  <button
                    onClick={() => removeMember(member.user_id)}
                    style={{
                      padding: '8px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: EMZI_COLORS.error,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderPermissionsTab = () => (
    <div style={{
      background: 'white',
      borderRadius: EMZI_RADIUS.md,
      padding: '24px',
      boxShadow: EMZI_SHADOWS.sm
    }}>
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ 
          fontSize: '18px', 
          fontWeight: '600', 
          marginBottom: '8px',
          color: EMZI_COLORS.textPrimary
        }}>
          {t('admin.permissions.title')}
        </h3>
        <p style={{ 
          fontSize: '14px', 
          color: EMZI_COLORS.textSecondary,
          marginBottom: '0'
        }}>
          {t('admin.permissions.toggleAccess')}
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
        {MODULES.map((module) => {
          const isEnabled = permissions.includes(module.id);
          
          return (
            <div
              key={module.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px',
                border: `2px solid ${isEnabled ? EMZI_COLORS.primary : EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                background: isEnabled ? `${EMZI_COLORS.primary}08` : 'white',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => togglePermission(module.id)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Shield 
                  size={20} 
                  color={isEnabled ? EMZI_COLORS.primary : EMZI_COLORS.textSecondary}
                />
                <span style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: isEnabled ? EMZI_COLORS.primary : EMZI_COLORS.textPrimary
                }}>
                  {module.name}
                </span>
              </div>

              <div
                style={{
                  width: '48px',
                  height: '24px',
                  borderRadius: '12px',
                  background: isEnabled ? EMZI_COLORS.primary : EMZI_COLORS.border,
                  position: 'relative',
                  transition: 'background 0.2s'
                }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: 'white',
                    position: 'absolute',
                    top: '2px',
                    left: isEnabled ? '26px' : '2px',
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={savePermissions}
        disabled={permissionsSaving}
        style={{
          width: '100%',
          padding: '14px',
          background: permissionsSaving ? EMZI_COLORS.textSecondary : EMZI_COLORS.primary,
          color: 'white',
          border: 'none',
          borderRadius: EMZI_RADIUS.sm,
          fontSize: '15px',
          fontWeight: '600',
          cursor: permissionsSaving ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        <Check size={18} />
        {permissionsSaving ? t('common.saving') : t('admin.permissions.savePermissions')}
      </button>
    </div>
  );

  const renderActivityTab = () => (
    <>
      {/* Header Section */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ 
          fontSize: '16px', 
          fontWeight: '600', 
          marginBottom: '4px',
          color: EMZI_COLORS.textPrimary
        }}>
          {t('admin.activity.title')}
        </h3>
        <p style={{ 
          fontSize: '13px', 
          color: EMZI_COLORS.textSecondary,
          margin: 0
        }}>
          {t('admin.activity.subtitle')}
        </p>
      </div>

      {/* Content Section */}
      <div style={{
        background: 'white',
        borderRadius: EMZI_RADIUS.md,
        border: `1px solid ${EMZI_COLORS.border}`,
        overflow: 'hidden',
        boxShadow: EMZI_SHADOWS.sm
      }}>
        {logsLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: EMZI_COLORS.textSecondary }}>
            <Activity size={40} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
            <div style={{ fontSize: '14px' }}>{t('common.loading')}</div>
          </div>
        ) : activityLogs.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '80px 20px',
            color: EMZI_COLORS.textSecondary
          }}>
            <Activity size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
            <div style={{ 
              fontSize: '14px', 
              fontWeight: '500',
              color: '#9ca3af'
            }}>
              Tiada aktiviti direkodkan lagi
            </div>
          </div>
        ) : (
          <div style={{
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ 
                  background: EMZI_COLORS.background,
                  borderBottom: `2px solid ${EMZI_COLORS.border}`
                }}>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: EMZI_COLORS.textSecondary,
                    textTransform: 'uppercase'
                  }}>
                    {t('admin.activity.action')}
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: EMZI_COLORS.textSecondary,
                    textTransform: 'uppercase'
                  }}>
                    {t('admin.activity.user')}
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: EMZI_COLORS.textSecondary,
                    textTransform: 'uppercase'
                  }}>
                    {t('admin.activity.admin')}
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: EMZI_COLORS.textSecondary,
                    textTransform: 'uppercase'
                  }}>
                    {t('admin.activity.timestamp')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {activityLogs.map((log, index) => (
                  <tr 
                    key={log.log_id || index}
                    style={{
                      borderBottom: index < activityLogs.length - 1 ? `1px solid ${EMZI_COLORS.border}` : 'none',
                      background: 'white'
                    }}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        fontSize: '11px',
                        padding: '3px 8px',
                        borderRadius: '10px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}
                      className={getActionBadgeColor(log.action)}
                      >
                        {t(`admin.activity.actions.${log.action}`) || log.action}
                      </span>
                    </td>
                    <td style={{ 
                      padding: '12px 16px',
                      fontSize: '13px',
                      color: EMZI_COLORS.textPrimary
                    }}>
                      {log.user_email || 'N/A'}
                    </td>
                    <td style={{ 
                      padding: '12px 16px',
                      fontSize: '13px',
                      color: EMZI_COLORS.textSecondary
                    }}>
                      {log.actor || 'System'}
                    </td>
                    <td style={{ 
                      padding: '12px 16px',
                      fontSize: '12px',
                      color: EMZI_COLORS.textSecondary,
                      whiteSpace: 'nowrap'
                    }}>
                      {formatTimestamp(log.timestamp)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );

  // ============================================================
  // MAIN RENDER
  // ============================================================

  if (!group) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: EMZI_RADIUS.lg,
        width: '100%',
        maxWidth: '900px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: EMZI_SHADOWS.header
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: `2px solid ${EMZI_COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Users size={24} color={EMZI_COLORS.primary} />
            <div>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: EMZI_COLORS.textPrimary,
                marginBottom: '4px'
              }}>
                {t('admin.members.manageMembers')}
              </h2>
              <p style={{
                fontSize: '14px',
                color: EMZI_COLORS.textSecondary,
                margin: 0
              }}>
                {group.name}
              </p>
            </div>
          </div>
          
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              color: EMZI_COLORS.textSecondary
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: `2px solid ${EMZI_COLORS.border}`,
          padding: '0 24px',
          gap: '4px'
        }}>
          <button
            onClick={() => setActiveTab('members')}
            style={{
              padding: '14px 20px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'members' ? `3px solid ${EMZI_COLORS.primary}` : '3px solid transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: activeTab === 'members' ? EMZI_COLORS.primary : EMZI_COLORS.textSecondary,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              marginBottom: '-2px'
            }}
          >
            <Users size={18} />
            {t('admin.members.membersTab')}
          </button>

          <button
            onClick={() => setActiveTab('permissions')}
            style={{
              padding: '14px 20px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'permissions' ? `3px solid ${EMZI_COLORS.primary}` : '3px solid transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: activeTab === 'permissions' ? EMZI_COLORS.primary : EMZI_COLORS.textSecondary,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              marginBottom: '-2px'
            }}
          >
            <Shield size={18} />
            {t('admin.members.permissionsTab')}
          </button>

          <button
            onClick={() => setActiveTab('activity')}
            style={{
              padding: '14px 20px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'activity' ? `3px solid ${EMZI_COLORS.primary}` : '3px solid transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: activeTab === 'activity' ? EMZI_COLORS.primary : EMZI_COLORS.textSecondary,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              marginBottom: '-2px'
            }}
          >
            <Activity size={18} />
            {t('admin.members.activityTab')}
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px',
          background: EMZI_COLORS.background
        }}>
          {activeTab === 'members' && renderMembersTab()}
          {activeTab === 'permissions' && renderPermissionsTab()}
          {activeTab === 'activity' && renderActivityTab()}
        </div>

        {/* Toast Notification */}
        {toast && (
          <div style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            padding: '14px 20px',
            background: toast.type === 'success' ? EMZI_COLORS.success : EMZI_COLORS.error,
            color: 'white',
            borderRadius: EMZI_RADIUS.md,
            boxShadow: EMZI_SHADOWS.card,
            fontSize: '14px',
            fontWeight: '600',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <Check size={18} />
            {toast.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageGroupModal;
